import { useTranslation } from "react-i18next";
import { ChevronRight, Plus } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { FilterRuleProps } from "./AdvancedFilter";
import AdvancedFilterBuilderItem from "./AdvancedFilterBuilderItem";
import FilterDropdown from "./FilterDropdown";

type AdvancedFilterBuilderProps = {
  availableFields: {
    label: string;
    value: string;
    data: any;
  }[];
  filterRules: FilterRuleProps[];
  onAdd: (filterRule: FilterRuleProps) => void;
  onChange: (index: number, value: FilterRuleProps) => void;
  onRemove: (index: number) => void;
  onDeleteAdvancedFilter: () => void;
};

const AdvancedFilterBuilder = ({
  availableFields,
  filterRules,
  onAdd,
  onChange,
  onRemove,
  onDeleteAdvancedFilter,
}: AdvancedFilterBuilderProps) => {
  const { t } = useTranslation();

  const lastRule = filterRules[filterRules.length - 1];
  const isLastIncomplete =
    lastRule && (!lastRule.operator || !lastRule.fieldValue || lastRule.fieldValue.length === 0);

  return (
    <div className={"min-w-80 space-y-2"}>
      {filterRules.map((item, index) => {
        return (
          <AdvancedFilterBuilderItem
            key={`${item.fieldId}_${index}`}
            availableFields={availableFields}
            idx={index}
            filterRule={item}
            onChange={(filterRule) => {
              onChange(index, filterRule);
            }}
            onRemove={onRemove}
          />
        );
      })}
      <div
        className={`w-full flex items-center gap-2 ${
          filterRules.length > 0 ? "pt-2 border-t border-gray-300" : ""
        }`}
      >
        <div className="flex items-center">
          <div
            className="w-max px-2 py-1 text-xs text-gray-600 rounded-full border border-gray-300 hover:bg-gray-50 cursor-pointer"
            onClick={onDeleteAdvancedFilter}
          >
            {t("Delete Filter")}
          </div>
          <div className="h-6 pl-4 border-r border-gray-300"></div>
        </div>
        <Popover className="w-full">
          <PopoverButton
            className={`w-full flex items-center justify-between outline-none ${
              isLastIncomplete ? "cursor-not-allowed opacity-40" : "cursor-pointer"
            }`}
            disabled={!!isLastIncomplete}
          >
            <div className="px-2 text-sm">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                {t("Add filter")}
              </div>
            </div>
            <ChevronRight className="w-5 h-5" />
          </PopoverButton>
          <PopoverPanel
            anchor="right start"
            className="[--anchor-gap:20px] z-20 p-2 bg-white rounded-md border border-gray-300 shadow-xl outline-none"
          >
            {({ close }) => (
              <FilterDropdown
                fields={availableFields}
                isFilter={true}
                onClick={(value) => {
                  onAdd(value);
                }}
                onClose={close}
              />
            )}
          </PopoverPanel>
        </Popover>
      </div>
    </div>
  );
};

export default AdvancedFilterBuilder;
